import { useCallback, useMemo, useState, type ChangeEvent } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Table, Thead, Tbody, Tr, Td, Th, TableScrollHost } from '@/common/ui/Table'
import { ListFilter, ListSort } from '@/common/ui/listToolbar'
import { Card } from '@/common/ui/Card/Card'
import { ListTableEmptyOverlay } from '@/common/ui/ListTableEmptyOverlay/ListTableEmptyOverlay'
import { Button } from '@/common/ui/Form/Button/Button'
import { Pagination } from '@/common/ui/Pagination'
import { usePagination } from '@/common/hooks/usePagination'
import { defaultUrlListQueryParams, useUrlListFilters } from '@/common/hooks/useUrlListFilters'
import {
    useCompanyStatsProductsScores,
    type CompanyStatsProductsScoresOrderBy,
    type CompanyStatsProductsScoresTarget,
} from '../model'
import type { CompanyStatsDataHooks } from '../model/hooks'
import { fetchAllCompanyStatsProductsScores } from '../lib/fetchAllProductsScores'
import {
    collectTableColumns,
    exportProductsScoresToExcel,
    findBlock,
    findCriteria,
    formatBlockPercent,
    isCriteriaPassed,
    type ProductScoresTableColumn,
} from '../lib/productScoresTable'
import { CompanyStatsBlockPercentCell } from './CompanyStatsBlockPercentCell'
import { CompanyStatsExcelExportButton } from './CompanyStatsExcelExportButton'
import { CompanyStatsProductScorePill } from './CompanyStatsProductScorePill'

const PRODUCTS_SCORES_ORDER_BY_ALLOWLIST = ['score', 'updated_at'] as const
type ProductsScoresOrderByKey = (typeof PRODUCTS_SCORES_ORDER_BY_ALLOWLIST)[number]

const PRODUCTS_SCORES_SORT_FIELD_OPTIONS: { key: ProductsScoresOrderByKey; label: string }[] = [
    { key: 'score', label: 'Оценка' },
    { key: 'updated_at', label: 'Дата обновления' },
]

const TARGET_FILTER_OPTIONS = [
    { value: '', label: 'Все' },
    { value: 'best', label: 'Хорошие (8+)' },
    { value: 'middle', label: 'Средние (6–7)' },
    { value: 'low', label: 'Слабые (≤5)' },
] as const

const TARGET_ALLOWLIST = ['best', 'middle', 'low'] as const

const isCustomProductsScoresSort = (orderBy: string, direction: 'asc' | 'desc') =>
    orderBy !== 'score' || direction !== 'desc'

const parseTargetFromParams = (raw: string | null): CompanyStatsProductsScoresTarget | undefined => {
    const trimmed = raw?.trim()
    if (!trimmed) return undefined
    if (!(TARGET_ALLOWLIST as readonly string[]).includes(trimmed)) return undefined
    return trimmed as CompanyStatsProductsScoresTarget
}

type CompanyStatsAllProductsTableProps = {
    orgId: string
    brandId?: string
    sellerId?: string
    hooks?: Pick<CompanyStatsDataHooks, 'useCompanyStatsProductsScores'>
}

export const CompanyStatsAllProductsTable = ({
    orgId,
    brandId,
    sellerId,
    hooks = { useCompanyStatsProductsScores },
}: CompanyStatsAllProductsTableProps) => {
    const [searchParams, setSearchParams] = useSearchParams()

    const urlFilters = useUrlListFilters({
        queryParams: defaultUrlListQueryParams,
        orderByAllowlist: [...PRODUCTS_SCORES_ORDER_BY_ALLOWLIST],
        defaultOrderBy: 'score',
        defaultDirection: 'desc',
    })

    const targetFromUrl = useMemo(() => parseTargetFromParams(searchParams.get('target')), [searchParams])

    const filterSignature = useMemo(
        () => [urlFilters.filterKey, targetFromUrl ?? '', brandId ?? '', sellerId ?? ''].join('|'),
        [urlFilters.filterKey, targetFromUrl, brandId, sellerId]
    )

    const { currentPage, pageSize, setCurrentPage } = usePagination({
        pageSize: 10,
        resetDeps: [filterSignature],
    })

    const { data, isLoading, error, refetch } = hooks.useCompanyStatsProductsScores(orgId, {
        page: currentPage,
        size: pageSize,
        order_by: urlFilters.orderBy as CompanyStatsProductsScoresOrderBy,
        direction: urlFilters.direction,
        target: targetFromUrl,
        brand: brandId,
        seller: sellerId,
    })

    const listItems = useMemo(() => data?.items ?? [], [data?.items])
    const tableColumns = useMemo(() => collectTableColumns(listItems), [listItems])
    const totalPages = data?.pages ?? 0
    const isEmptyList = !isLoading && listItems.length === 0
    const hasTargetFilterActive = Boolean(targetFromUrl)

    const [targetDraft, setTargetDraft] = useState(searchParams.get('target') ?? '')
    const [isExporting, setIsExporting] = useState(false)

    const handleListFilterBeforeOpen = useCallback(() => {
        setTargetDraft(searchParams.get('target') ?? '')
    }, [searchParams])

    const handleApplyFilters = useCallback(() => {
        setSearchParams(
            prev => {
                const next = new URLSearchParams(prev)
                const trimmed = targetDraft.trim()
                if (trimmed && (TARGET_ALLOWLIST as readonly string[]).includes(trimmed)) {
                    next.set('target', trimmed)
                } else {
                    next.delete('target')
                }
                next.delete('scroll')
                return next
            },
            { replace: true }
        )
    }, [targetDraft, setSearchParams])

    const handleClearFilters = useCallback(() => {
        setTargetDraft('')
        setSearchParams(
            prev => {
                const next = new URLSearchParams(prev)
                next.delete('target')
                next.delete('scroll')
                return next
            },
            { replace: true }
        )
    }, [setSearchParams])

    const handleClearAllFiltersForEmpty = useCallback(() => {
        setTargetDraft('')
        setSearchParams(
            prev => {
                const next = new URLSearchParams(prev)
                next.delete('target')
                next.delete('scroll')
                return next
            },
            { replace: true }
        )
    }, [setSearchParams])

    const handleSortFieldSelectChange = useCallback(
        (e: ChangeEvent<HTMLSelectElement>) => {
            const field = e.target.value as ProductsScoresOrderByKey
            setSearchParams(
                prev => {
                    const next = new URLSearchParams(prev)
                    if (urlFilters.orderBy === field) {
                        const newDir = urlFilters.direction === 'desc' ? 'asc' : 'desc'
                        if (newDir === 'desc') next.delete('direction')
                        else next.set('direction', 'asc')
                    } else {
                        if (field === 'score') next.delete('order_by')
                        else next.set('order_by', field)
                        if (field === 'score') next.delete('direction')
                        else next.delete('direction')
                    }
                    next.delete('scroll')
                    return next
                },
                { replace: true }
            )
        },
        [setSearchParams, urlFilters.orderBy, urlFilters.direction]
    )

    const handleExport = useCallback(async () => {
        if (!orgId || isExporting) return
        setIsExporting(true)
        try {
            const products = await fetchAllCompanyStatsProductsScores(orgId, {
                order_by: urlFilters.orderBy as CompanyStatsProductsScoresOrderBy,
                direction: urlFilters.direction,
                target: targetFromUrl,
                brand: brandId,
                seller: sellerId,
            })
            if (products.length === 0) return
            exportProductsScoresToExcel(products, 'company_stats_products')
        } finally {
            setIsExporting(false)
        }
    }, [brandId, sellerId, isExporting, orgId, targetFromUrl, urlFilters.direction, urlFilters.orderBy])

    const colSpan = 3 + tableColumns.length

    const renderTableHeader = (col: ProductScoresTableColumn) => {
        if (col.type === 'block') {
            return (
                <Th key={col.key} className='min-w-[7rem] text-[10px] leading-tight' title={col.header}>
                    {col.header}
                </Th>
            )
        }

        return (
            <Th
                key={col.key}
                className='min-w-[4rem] max-w-[8rem] text-center text-[10px] leading-tight'
                title={col.header}
            >
                {col.header}
            </Th>
        )
    }

    const renderTableCell = (product: (typeof listItems)[number], col: ProductScoresTableColumn) => {
        if (col.type === 'block') {
            const block = findBlock(product, col.blockType)
            const percent = formatBlockPercent(block)

            return (
                <Td key={col.key}>
                    {percent != null ? (
                        <CompanyStatsBlockPercentCell percent={percent} label={col.header} />
                    ) : (
                        <span className='text-base-content/40'>—</span>
                    )}
                </Td>
            )
        }

        const criteria = findCriteria(product, col)
        const passed = isCriteriaPassed(criteria)

        return (
            <Td key={col.key} className='text-center'>
                {passed ? (
                    <span className='text-[#08AC81] font-medium' aria-label={`${col.header}: выполнено`}>
                        ✓
                    </span>
                ) : (
                    <span className='text-base-content/30' aria-hidden>
                        —
                    </span>
                )}
            </Td>
        )
    }

    if (error) {
        return (
            <Card color='primary'>
                <div className='flex flex-col gap-4'>
                    <h3 className='text-xs font-bold uppercase tracking-wide text-white'>ВСЕ Товары</h3>
                    <div className='alert alert-error'>
                        <span>Ошибка загрузки: {error.message}</span>
                    </div>
                    <Button type='button' emphasis='accent' onClick={() => void refetch()}>
                        Попробовать снова
                    </Button>
                </div>
            </Card>
        )
    }

    return (
        <Card color='primary' tableScroll>
            <div className='flex flex-col gap-[1.25rem] mb-[1rem] shrink-0'>
                <div className='flex flex-wrap items-center gap-2'>
                    <h3 className='text-xs font-bold uppercase tracking-wide text-white'>ВСЕ Товары</h3>
                    <div className='flex-1 min-w-[120px]' />
                    <CompanyStatsExcelExportButton
                        onClick={() => void handleExport()}
                        disabled={(data?.total ?? 0) === 0}
                        isLoading={isExporting}
                        ariaLabel='Экспорт всех товаров в Excel'
                    />
                </div>
                <div className='flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between'>
                    <p className='text-base-content/70'>Всего товаров: {data?.total ?? 0}</p>
                    <div className='flex w-full min-w-0 justify-end flex-1 gap-4 flex-nowrap items-center'>
                        <ListFilter
                            active={hasTargetFilterActive}
                            onApply={handleApplyFilters}
                            onClear={handleClearFilters}
                            onBeforeOpen={handleListFilterBeforeOpen}
                            aria-label='Фильтр списка товаров по оценке'
                            filterSelectFields={[
                                {
                                    key: 'target',
                                    label: 'Категория оценки',
                                    placeholder: 'Все',
                                    ariaLabel: 'Категория оценки товара',
                                    options: TARGET_FILTER_OPTIONS.map(o => ({
                                        value: o.value,
                                        label: o.label,
                                    })),
                                },
                            ]}
                            filterValues={{ target: targetDraft }}
                            onFilterValueChange={(key, value) => {
                                if (key === 'target') setTargetDraft(value)
                            }}
                        />
                        <ListSort
                            options={PRODUCTS_SCORES_SORT_FIELD_OPTIONS}
                            value={urlFilters.orderBy}
                            direction={urlFilters.direction}
                            onChange={handleSortFieldSelectChange}
                            highlightActive={isCustomProductsScoresSort(urlFilters.orderBy, urlFilters.direction)}
                            aria-label='Сортировка списка товаров: поле и направление'
                        />
                    </div>
                </div>
            </div>

            <TableScrollHost>
                <Table pinRows striped thBg rounded={false} className='table-pin-left-2 w-full min-w-[48rem]'>
                    <Thead>
                        <Tr>
                            {[
                                <Th key='index' className='text-center'>
                                    #
                                </Th>,
                                <Th key='name'>Название / ссылка</Th>,
                                <Th key='total' className='w-24'>
                                    Итог
                                </Th>,
                                ...tableColumns.map(renderTableHeader),
                            ]}
                        </Tr>
                    </Thead>
                    <Tbody>
                        {isLoading && listItems.length === 0 ? (
                            <Tr>
                                <Td colSpan={colSpan} className='text-center py-8 text-base-content/70'>
                                    <span className='loading loading-spinner loading-md' aria-hidden />
                                </Td>
                            </Tr>
                        ) : (
                            listItems.map((product, index) => {
                                const rowNumber = (currentPage - 1) * pageSize + index + 1

                                return (
                                    <Tr key={product.id}>
                                        {[
                                            <Td
                                                key='index'
                                                className='text-center text-sm tabular-nums text-base-content/40'
                                            >
                                                {rowNumber}
                                            </Td>,
                                            <Td key='name'>
                                                <Link
                                                    to={`/app/products/${product.id}/analytics`}
                                                    className='text-sm leading-snug text-base-content/80 link link-hover line-clamp-2'
                                                    aria-label={`${product.name}, перейти к аналитике`}
                                                >
                                                    {product.name}
                                                </Link>
                                            </Td>,
                                            <Td key='total'>
                                                <CompanyStatsProductScorePill
                                                    score={product.score}
                                                    maxScore={product.max_score}
                                                />
                                            </Td>,
                                            ...tableColumns.map(col => renderTableCell(product, col)),
                                        ]}
                                    </Tr>
                                )
                            })
                        )}
                    </Tbody>
                </Table>
                <ListTableEmptyOverlay
                    show={isEmptyList}
                    hasActiveFilters={hasTargetFilterActive}
                    onClearFilters={handleClearAllFiltersForEmpty}
                    emptyLabel='Товаров нет'
                />
            </TableScrollHost>

            {totalPages > 1 && (
                <div className='mt-6 text-center'>
                    <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
                </div>
            )}
        </Card>
    )
}
